import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Heading,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { CheckCircle, FloppyDisk, Warning } from "@phosphor-icons/react";
import { useSession } from "../state/useSession";
import { useWorkingPanel } from "../state/useWorkingPanel";
import { buildCuratedPayload } from "../api/dataMappingClient";
import {
  useFoundationV2Years,
  useSaveFoundationV2Curated,
} from "../../../hooks/query/issb";

// The Foundation Data v2 twin of CommitPanel, and it keeps that panel's three invariants:
//
// 1. **Preview before write.** The dry run and the save take the same path server-side with one flag
//    between them, so the counts shown are the counts the write produces.
// 2. **It saves the rows that were previewed.** The save sends the same `request` object the dry run
//    returned, and any change — a different frame, a different mode — clears the verdict. A rebuilt
//    payload is a different payload even when it looks identical.
// 3. **The user saves, not the agent.** The agent has no save tool by design.
//
// It is much smaller than CommitPanel because the destination is fixed and needs no template, no
// methodology document and no per-field review: the curated rows have the same shape as the raw ones, and
// the user has been looking at them in the grid.

const MODE_HELP = {
  MERGE:
    "Rows in this session replace matching rows in the year. Anything the year has that this session " +
    "does not is left alone.",
  REPLACE:
    "This session becomes the complete curated set for the year. Rows the year has that this session " +
    "does not are archived.",
};

export const FoundationSavePanel = () => {
  const toast = useToast();
  const { sessionId, foundationYearId } = useSession();
  const { selectedOutputKey, dataframes } = useWorkingPanel();

  const [dfKey, setDfKey] = useState(selectedOutputKey || "");
  const [mode, setMode] = useState("MERGE");
  // Figures matching no datapoint in the org's target structure. Keep by default: dropping is the
  // irreversible half of the choice, so it is chosen rather than defaulted into.
  const [keepOffSchema, setKeepOffSchema] = useState(true);
  const [payload, setPayload] = useState(null);
  const [payloadError, setPayloadError] = useState(null);
  const [isBuilding, setBuilding] = useState(false);
  // The verdict of a dry run, holding the exact request that was checked. Save sends this and nothing
  // else, so a clean preview can never vouch for a payload rebuilt afterwards.
  const [preview, setPreview] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(null);

  const { data: years } = useFoundationV2Years();
  const { mutateAsync: saveCurated, isLoading: isSaving } = useSaveFoundationV2Curated();

  /*
    The save endpoint is keyed on the workstream row, which is exactly what the session is bound to — so
    `foundationYearId` goes straight through and nothing has to be resolved to write.

    The row is still looked up, for the LABEL: a button reading "Save curated data" tells the user nothing about
    where it lands, and "to GHG Emissions 2026" is the one thing they need confirmed before pressing it.
    Resolved through the year list rather than read from the URL, which a user can hand-edit into naming a
    different workstream than the session was built for.
  */
  const dataYear = useMemo(
    () => (years || []).find((y) => String(y.id) === String(foundationYearId)) ?? null,
    [years, foundationYearId],
  );
  const destination = dataYear ? `${dataYear.questionGroup} ${dataYear.year}` : null;

  // Every writable frame, not only ones named "*_output". Saving the extracted rows unchanged is a
  // legitimate outcome — "these look right as they are" — and filtering to a naming convention would hide
  // that option behind asking the agent to clone a frame for no reason.
  const candidates = useMemo(
    () => (dataframes || []).filter((d) => !d.readonly),
    [dataframes],
  );

  // Any change to what would be saved invalidates the preview, including a success banner: the user is
  // now looking at a different set of rows.
  const invalidate = useCallback(() => {
    setPreview(null);
    setSaveError(null);
    setSaved(null);
  }, []);

  const loadPayload = useCallback(async () => {
    if (!dfKey || !sessionId) {
      setPayload(null);
      return;
    }
    setBuilding(true);
    setPayloadError(null);
    invalidate();
    try {
      setPayload(await buildCuratedPayload(sessionId, { dfKey, mode }));
    } catch (err) {
      setPayload(null);
      setPayloadError(
        err?.response?.data?.detail || "Couldn't read that frame. Try refreshing the panel.",
      );
    } finally {
      setBuilding(false);
    }
  }, [sessionId, dfKey, mode, invalidate]);

  useEffect(() => {
    loadPayload();
  }, [loadPayload]);

  const runPreview = useCallback(
    async ({ keepOffSchema: keepOff = keepOffSchema } = {}) => {
      if (!payload?.curated_request || !foundationYearId) return;
      setSaveError(null);
      const request = { ...payload.curated_request, keepOffSchema: keepOff };
      try {
        const result = await saveCurated({ dataYearId: foundationYearId, request, dryRun: true });
        // The request is kept ALONGSIDE the verdict, with the count the preview reported folded in as
        // expectedReplacedRows. That is what makes the save refuse if a chat turn moved the year in
        // between, rather than silently archiving a different number of rows than the user agreed to.
        setPreview({
          ...result,
          request: { ...request, expectedReplacedRows: result.rowsSuperseded },
        });
      } catch (err) {
        setSaveError(
          err?.response?.data?.message || err?.message || "Couldn't preview this save.",
        );
      }
    },
    [payload, foundationYearId, saveCurated, keepOffSchema],
  );

  /*
    Changing the choice RE-PREVIEWS rather than editing the numbers on screen. Dropping rows changes both
    how many are written and how many existing rows they would have superseded, and expectedReplacedRows is
    compared against exactly that second number — so an adjusted-in-place preview would send a stale count
    and earn a 409 on a year nobody else had touched.
  */
  const chooseOffSchema = useCallback(
    async (keep) => {
      setKeepOffSchema(keep);
      await runPreview({ keepOffSchema: keep });
    },
    [runPreview],
  );

  const commit = useCallback(async () => {
    if (!preview?.request || !foundationYearId) return;
    setSaveError(null);
    try {
      const result = await saveCurated({
        dataYearId: foundationYearId,
        request: preview.request,
        dryRun: false,
      });
      setSaved(result);
      toast({
        title: `Saved ${result.rowsWritten} curated row${
          result.rowsWritten === 1 ? "" : "s"
        } to ${destination ?? "Foundation Data"}`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      const status = err?.response?.status;
      /*
        Since ESG-4012 the server also refuses a save the workflow does not allow — 409 past the editing
        stages, 403 for someone who is not the owner or an assignee — and its message says which. That is not
        knowable from this page (it holds no workstream question), so the server's wording is shown as-is.
      */
      const serverMessage = err?.response?.data?.parameters?.message;
      setSaveError(
        serverMessage ||
          (status === 409
            ? "This changed while you were reviewing. Preview again to see the new numbers."
            : err?.response?.data?.message || "Couldn't save. Try previewing again."),
      );
      // A 409 or 403 cannot succeed on retry, so the preview must not remain clickable.
      if (status === 409 || status === 403) setPreview(null);
    }
  }, [preview, foundationYearId, destination, saveCurated, toast]);

  if (!candidates.length) {
    return (
      <Box p={8}>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="600">Nothing to save yet</Text>
            <Text fontSize="body.xs" color="gray.600">
              Ask the assistant to build a curated output frame, then come back here.
            </Text>
          </Box>
        </Alert>
      </Box>
    );
  }

  const issues = payload?.issues || [];

  return (
    <Box p={8} overflowY="auto">
      <VStack align="stretch" spacing={6} maxW="720px">
        <Box>
          <Heading size="sm" color="gray.700">
            Save curated data{destination ? ` to ${destination}` : ""}
          </Heading>
          <Text fontSize="body.xs" color="gray.500">
            The extracted rows are never changed by this. What you save here is kept separately, so the
            original figures from the source document stay exactly as they were.
          </Text>
        </Box>

        <Box>
          <Text fontSize="body.xs" fontWeight="600" color="gray.600" mb={1}>
            Which frame
          </Text>
          <Select
            size="sm"
            value={dfKey}
            onChange={(e) => setDfKey(e.target.value)}
            placeholder="Choose the frame to save"
            _focus={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
            _focusVisible={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
          >
            {candidates.map((d) => (
              <option key={d.key} value={d.key}>
                {d.key} ({d.rows} rows)
              </option>
            ))}
          </Select>
        </Box>

        <Box>
          <Text fontSize="body.xs" fontWeight="600" color="gray.600" mb={1}>
            How to apply it
          </Text>
          <RadioGroup
            value={mode}
            onChange={(next) => {
              setMode(next);
              invalidate();
            }}
          >
            <VStack align="stretch" spacing={2}>
              {["MERGE", "REPLACE"].map((option) => (
                <Radio key={option} value={option} colorScheme="green">
                  <Text fontSize="body.s" fontWeight="600">
                    {option === "MERGE" ? "Merge" : "Replace"}
                  </Text>
                  <Text fontSize="body.xs" color="gray.500">
                    {MODE_HELP[option]}
                  </Text>
                </Radio>
              ))}
            </VStack>
          </RadioGroup>
        </Box>

        {isBuilding && (
          <HStack color="gray.600">
            <Spinner size="sm" />
            <Text fontSize="body.xs">Reading the frame…</Text>
          </HStack>
        )}

        {payloadError && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Text fontSize="body.xs">{payloadError}</Text>
          </Alert>
        )}

        {issues.length > 0 && (
          <Alert status="warning" borderRadius="md" alignItems="flex-start">
            <AlertIcon as={Warning} />
            <Box>
              <Text fontWeight="600" fontSize="body.s">
                This frame is not ready to save
              </Text>
              <VStack align="stretch" spacing={0} mt={1}>
                {issues.slice(0, 8).map((issue, index) => (
                  <Text key={index} fontSize="body.xs" color="gray.700">
                    • {issue.message}
                  </Text>
                ))}
              </VStack>
              {issues.length > 8 && (
                <Text fontSize="body.xs" color="gray.500" mt={1}>
                  …and {issues.length - 8} more.
                </Text>
              )}
            </Box>
          </Alert>
        )}

        {payload?.committable && !preview && (
          <Box>
            <Divider mb={4} />
            <HStack justify="space-between">
              <Text fontSize="body.xs" color="gray.600">
                {payload.row_count} row{payload.row_count === 1 ? "" : "s"} ready.
              </Text>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => runPreview()}
                isLoading={isSaving}
                isDisabled={!foundationYearId}
              >
                Preview this save
              </Button>
            </HStack>
          </Box>
        )}

        {preview && !saved && (
          <Box>
            <Divider mb={4} />
            <HStack spacing={8} mb={3}>
              <Stat>
                <StatLabel fontSize="body.xs" color="gray.500">
                  Rows saved
                </StatLabel>
                <StatNumber fontSize="heading.m">{preview.rowsWritten}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel fontSize="body.xs" color="gray.500">
                  Rows archived
                </StatLabel>
                <StatNumber fontSize="heading.m">{preview.rowsSuperseded}</StatNumber>
              </Stat>
              {preview.rowsSkipped > 0 && (
                <Stat>
                  <StatLabel fontSize="body.xs" color="gray.500">
                    Skipped
                  </StatLabel>
                  <StatNumber fontSize="heading.m">{preview.rowsSkipped}</StatNumber>
                </Stat>
              )}
            </HStack>
            {preview.rowsSuperseded > 0 && (
              <Text fontSize="body.xs" color="gray.600" mb={3}>
                Archived rows are not deleted — they stay recoverable.
              </Text>
            )}
            {/* Only when there are any. An org with no target structure never sees this, and neither does a
                session whose rows all matched — a choice with nothing turning on it teaches the reader to
                click past the one that matters. */}
            {preview.rowsOffSchema > 0 && (
              <Box
                borderWidth="1px"
                borderColor="orange.200"
                bg="orange.50"
                borderRadius="md"
                p={3}
                mb={3}
              >
                <Text fontSize="body.s" fontWeight="600" color="orange.800">
                  {preview.rowsOffSchema} row{preview.rowsOffSchema === 1 ? "" : "s"} match no datapoint in
                  your target structure
                </Text>
                <RadioGroup
                  mt={2}
                  value={keepOffSchema ? "keep" : "drop"}
                  onChange={(next) => chooseOffSchema(next === "keep")}
                >
                  <VStack align="stretch" spacing={1}>
                    <Radio value="keep" colorScheme="green">
                      <Text fontSize="body.xs">
                        Keep them — shown in their own section below the structure
                      </Text>
                    </Radio>
                    <Radio value="drop" colorScheme="green">
                      <Text fontSize="body.xs">
                        Leave them out — the source document still records them
                      </Text>
                    </Radio>
                  </VStack>
                </RadioGroup>
              </Box>
            )}
            <Button
              size="sm"
              variant="primary"
              leftIcon={<FloppyDisk size={14} />}
              onClick={commit}
              isLoading={isSaving}
            >
              Save {preview.rowsWritten} row{preview.rowsWritten === 1 ? "" : "s"}
            </Button>
          </Box>
        )}

        {saved && (
          <Alert status="success" borderRadius="md" alignItems="flex-start">
            <AlertIcon as={CheckCircle} />
            <Box>
              <Text fontWeight="600" fontSize="body.s">
                Saved to {destination ?? "Foundation Data"}
              </Text>
              <Text fontSize="body.xs" color="gray.700">
                {saved.rowsWritten} row{saved.rowsWritten === 1 ? "" : "s"} written
                {saved.rowsSuperseded > 0 ? `, ${saved.rowsSuperseded} archived` : ""}. The Curated view
                on the Foundation Data page now shows this.
              </Text>
              <Badge mt={2} colorScheme="green">
                {saved.mode === "REPLACE" ? "Replaced the year" : "Merged into the year"}
              </Badge>
            </Box>
          </Alert>
        )}

        {saveError && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Text fontSize="body.xs">{saveError}</Text>
          </Alert>
        )}

        {!dataYear && (
          <Flex>
            <Text fontSize="body.xs" color="gray.500">
              Still loading this session&apos;s workstream…
            </Text>
          </Flex>
        )}
      </VStack>
    </Box>
  );
};
